<template>
    <ButtonPopup
        :button-class="[
            'tiptap-btn',
            { 'is-active': editor.isActive('link') }
        ]"
        :title="$t('Insert link')"
        popup-class="popup-content"
        :disabled="disabled || !editor.can().setLink"
        lazy
        @open="openLinkPopup">
        <LinkIcon size="16"/>
        <template #popup="{ close }">
            <form novalidate @submit.prevent="formSubmitHandler(close)">
                <div class="input-group">
                    <input
                        v-model="form.linkText"
                        v-focus
                        type="url"
                        class="form-control"
                        :class="{'is-invalid': $v.form.linkText.$error}"
                        :placeholder="$t('Enter address')">
                    <div class="input-group-append">
                        <button type="submit" class="btn btn-primary">
                            <LinkVariantPlusIcon size="16"/>
                        </button>
                        <button
                            v-if="editor.isActive('link')"
                            type="button"
                            class="btn btn-danger"
                            :title="$t('Remove link')"
                            @click="removeLink(close)">
                            <LinkOffIcon size="16"/>
                        </button>
                    </div>
                </div>
                <ValitationMessages :validation="$v.form.linkText"/>
            </form>
        </template>
    </ButtonPopup>
</template>

<script>
import ButtonPopup from '@/common/components/button-popup/ButtonPopup.vue';
import { Editor } from '@tiptap/vue-2';
import {email, helpers, required, url as isUrl} from 'vuelidate/lib/validators';
import LinkIcon from '@/common/components/svg-icons/LinkIcon.vue';
import LinkOffIcon from '@/common/components/svg-icons/LinkOffIcon.vue';
import LinkVariantPlusIcon from '@/common/components/svg-icons/LinkVariantPlusIcon.vue';

export default {
    name: 'MenuBarLinkButton',

    props: {
        editor: {
            type: Editor,
            required: true
        },
        disabled: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            form: {
                linkText: ''
            }
        };
    },

    methods: {
        getSelectedText() {
            const { state } = this.editor;
            const { from, to } = state.selection;
            return state.doc.textBetween(from, to, ' ');
        },

        openLinkPopup() {
            const previousLink = this.editor.getAttributes('link').href;
            this.form.linkText = (previousLink || this.getSelectedText() || '').trim();
            this.$v.$reset();
        },

        addHttpsOrMailtoPrefix(value) {
            if (value == null) {
                return value;
            }

            const withoutPrefix = String(value).trim()
                .replace(/^(?:[a-z][a-z\d+.-]*:\/\/|\/\/|mailto:)/i, '');
            if (!withoutPrefix) {
                return withoutPrefix;
            }

            const address = withoutPrefix.split('?')[0];
            if (helpers.req(address) && email(address)) {
                return `mailto:${address}`;
            }

            return `https://${withoutPrefix}`;
        },

        isAllowedLink(value) {
            const href = this.addHttpsOrMailtoPrefix(value);
            const mailtoPattern = /^mailto:([^?]*)(\?.*)?$/i;
            const mailto = href && mailtoPattern.exec(href);
            if (mailto) {
                return helpers.req(mailto[1]) && email(mailto[1]);
            }

            return !!href && /^https:\/\//i.test(href) && isUrl(href);
        },

        formSubmitHandler(close) {
            this.$v.form.$touch();
            if (this.$v.form.$invalid) {
                return;
            }

            const href = this.addHttpsOrMailtoPrefix(this.form.linkText);
            this.form.linkText = href;
            this.editor.chain().focus().setLink({ href }).run();
            close();
        },

        removeLink(close) {
            this.editor.chain().focus().extendMarkRange('link').unsetLink().run();
            close();
        }
    },

    components: {
        LinkOffIcon,
        LinkVariantPlusIcon,
        LinkIcon,
        ButtonPopup
    },

    validations: {
        form: {
            linkText: {
                required,
                url: helpers.withParams({type: 'url'}, function (value) {
                    if (!helpers.req(value)) {
                        return true;
                    }

                    return this.isAllowedLink(value);
                })
            }
        }
    }
};
</script>
